import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ACTIVATION_READINESS_THRESHOLDS } from './activation-readiness.config';

/** Raw evidence pulled from existing Firebase / APIs / meta — not journey flags. */
export interface ActivationEvidenceSnapshot {
  packagePrepared: boolean;
  privateTestEmployeeId: number | null;
  firstEmployeeId: number | null;
  agentAuthenticated: boolean;
  /** True when last heartbeat is within recentHeartbeatMaxAgeMs. */
  recentHeartbeat: boolean;
  onlineNow: boolean;
  firstScreenshotReceived: boolean;
  /** null = unknown / not checked; false = metadata without displayable pixels. */
  screenshotPixelsDisplayable: boolean | null;
  screenshotCount: number;
  periodicScreenshotsEnabled: boolean;
  appLogSessionCount: number;
  trackedActivityMinutes: number;
  firstEventAt: string | null;
  lastEventAt: string | null;
  fatalIngestOrAuthError: boolean;
  /** Shift ended, or employee-local calendar day ended when no shift. */
  shiftEndedOrDayEnded: boolean;
  dailyInsightRecipientEmails: string[];
  planOrTrialEligible: boolean;
}

export interface ChannelReadiness {
  id:
    | 'package'
    | 'heartbeat'
    | 'screenshot'
    | 'logs'
    | 'hours'
    | 'span'
    | 'recipient'
    | 'plan';
  ready: boolean;
  missingReason: string | null;
}

export interface ActivationReadinessResult {
  /** True when evaluate was given a real snapshot (not empty defaults-only). */
  hasEvidenceInput: boolean;
  packagePrepared: boolean;
  agentOnline: boolean;
  firstSignal: boolean;
  screenshotReady: boolean;
  screenshotPixelsDisplayable: boolean | null;
  logsReady: boolean;
  hoursReady: boolean;
  founderDayReady: boolean;
  earlyReviewReady: boolean;
  dailyInsightsEligible: boolean;
  eventSpanMinutes: number | null;
  channels: ChannelReadiness[];
  blockers: string[];
}

export function emptyEvidenceSnapshot(): ActivationEvidenceSnapshot {
  return {
    packagePrepared: false,
    privateTestEmployeeId: null,
    firstEmployeeId: null,
    agentAuthenticated: false,
    recentHeartbeat: false,
    onlineNow: false,
    firstScreenshotReceived: false,
    screenshotPixelsDisplayable: null,
    screenshotCount: 0,
    periodicScreenshotsEnabled: true,
    appLogSessionCount: 0,
    trackedActivityMinutes: 0,
    firstEventAt: null,
    lastEventAt: null,
    fatalIngestOrAuthError: false,
    shiftEndedOrDayEnded: false,
    dailyInsightRecipientEmails: [],
    planOrTrialEligible: false,
  };
}

export function eventSpanMinutes(
  firstEventAt: string | null,
  lastEventAt: string | null
): number | null {
  if (!firstEventAt || !lastEventAt) {
    return null;
  }
  const first = Date.parse(firstEventAt);
  const last = Date.parse(lastEventAt);
  if (!Number.isFinite(first) || !Number.isFinite(last) || last < first) {
    return null;
  }
  return (last - first) / (60 * 1000);
}

function validatedRecipients(emails: string[] | null | undefined): string[] {
  if (!Array.isArray(emails)) {
    return [];
  }
  return emails
    .filter((e): e is string => typeof e === 'string')
    .map((e) => e.trim())
    .filter((e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
}

/**
 * Pure readiness evaluation from an evidence snapshot + thresholds.
 * Journey flags (finished, continuedLater, etc.) must not be passed here.
 */
export function evaluateActivationReadiness(
  snapshot: ActivationEvidenceSnapshot,
  opts?: { hasEvidenceInput?: boolean }
): ActivationReadinessResult {
  const t = ACTIVATION_READINESS_THRESHOLDS;
  const span = eventSpanMinutes(snapshot.firstEventAt, snapshot.lastEventAt);
  const recipients = validatedRecipients(snapshot.dailyInsightRecipientEmails);
  const blockers: string[] = [];
  const channels: ChannelReadiness[] = [];

  const packageReady = !!snapshot.packagePrepared;
  channels.push({
    id: 'package',
    ready: packageReady,
    missingReason: packageReady ? null : 'Install package not prepared yet',
  });

  const heartbeatReady = !!(snapshot.agentAuthenticated && snapshot.recentHeartbeat);
  channels.push({
    id: 'heartbeat',
    ready: heartbeatReady,
    missingReason: heartbeatReady
      ? null
      : snapshot.agentAuthenticated
        ? 'No recent agent heartbeat'
        : 'Agent has not authenticated',
  });

  const screenshotCountOk =
    !snapshot.periodicScreenshotsEnabled ||
    snapshot.screenshotCount >= t.earlyReview.minScreenshotsWhenEnabled;
  const screenshotReady = !!snapshot.firstScreenshotReceived || snapshot.screenshotCount > 0;
  channels.push({
    id: 'screenshot',
    ready: screenshotReady,
    missingReason: screenshotReady ? null : 'No screenshot acknowledgement yet',
  });

  const logsReady = snapshot.appLogSessionCount >= 1;
  channels.push({
    id: 'logs',
    ready: logsReady,
    missingReason: logsReady ? null : 'No application log sessions yet',
  });

  const hoursReady = snapshot.trackedActivityMinutes > 0;
  channels.push({
    id: 'hours',
    ready: hoursReady,
    missingReason: hoursReady ? null : 'No tracked activity minutes yet',
  });

  const spanReady = span != null && span > 0;
  channels.push({
    id: 'span',
    ready: spanReady,
    missingReason: spanReady ? null : 'No first-to-last event span yet',
  });

  const recipientReady = recipients.length > 0;
  channels.push({
    id: 'recipient',
    ready: recipientReady,
    missingReason: recipientReady ? null : 'No validated Daily Insights recipient',
  });

  const planReady = !!snapshot.planOrTrialEligible;
  channels.push({
    id: 'plan',
    ready: planReady,
    missingReason: planReady ? null : 'Plan or trial not eligible for Daily Insights',
  });

  if (snapshot.fatalIngestOrAuthError) {
    blockers.push('Fatal ingest or auth error is active');
  }

  const firstSignal =
    !snapshot.fatalIngestOrAuthError &&
    (screenshotReady || logsReady || hoursReady);

  const realEmployeeDistinct =
    !!snapshot.firstEmployeeId &&
    snapshot.firstEmployeeId !== snapshot.privateTestEmployeeId;

  const earlyReviewReady =
    !snapshot.fatalIngestOrAuthError &&
    realEmployeeDistinct &&
    heartbeatReady &&
    span != null &&
    span >= t.earlyReview.minEventSpanMinutes &&
    snapshot.trackedActivityMinutes >= t.earlyReview.minTrackedActivityMinutes &&
    snapshot.appLogSessionCount >= t.earlyReview.minAppLogSessions &&
    screenshotCountOk;

  if (!realEmployeeDistinct) {
    blockers.push('First real employee (distinct from private test) required for early review');
  }
  if (!heartbeatReady) {
    blockers.push('Recent authenticated heartbeat required');
  }
  if (span == null || span < t.earlyReview.minEventSpanMinutes) {
    blockers.push(
      `Need at least ${t.earlyReview.minEventSpanMinutes} minutes of captured event span`
    );
  }
  if (snapshot.trackedActivityMinutes < t.earlyReview.minTrackedActivityMinutes) {
    blockers.push(
      `Need at least ${t.earlyReview.minTrackedActivityMinutes} tracked activity minutes`
    );
  }
  if (snapshot.appLogSessionCount < t.earlyReview.minAppLogSessions) {
    blockers.push(`Need at least ${t.earlyReview.minAppLogSessions} application-log sessions`);
  }
  if (snapshot.periodicScreenshotsEnabled && !screenshotCountOk) {
    blockers.push(
      `Need at least ${t.earlyReview.minScreenshotsWhenEnabled} stored screenshots`
    );
  }

  const founderScreenshotOk =
    !snapshot.periodicScreenshotsEnabled ||
    snapshot.screenshotCount >= t.founderDay.minScreenshotsWhenEnabled;
  const founderDayReady =
    !snapshot.fatalIngestOrAuthError &&
    !!snapshot.privateTestEmployeeId &&
    heartbeatReady &&
    span != null &&
    span >= t.founderDay.minEventSpanMinutes &&
    snapshot.trackedActivityMinutes >= t.founderDay.minTrackedActivityMinutes &&
    snapshot.appLogSessionCount >= t.founderDay.minAppLogSessions &&
    founderScreenshotOk;

  const dailyInsightsEligible =
    earlyReviewReady &&
    !!snapshot.shiftEndedOrDayEnded &&
    span != null &&
    span >= t.dailyInsights.minEventSpanMinutes &&
    recipientReady &&
    planReady;

  return {
    hasEvidenceInput: !!opts?.hasEvidenceInput,
    packagePrepared: packageReady,
    agentOnline: !!(snapshot.onlineNow || heartbeatReady),
    firstSignal,
    screenshotReady,
    screenshotPixelsDisplayable: snapshot.screenshotPixelsDisplayable,
    logsReady,
    hoursReady,
    founderDayReady,
    earlyReviewReady,
    dailyInsightsEligible,
    eventSpanMinutes: span,
    channels,
    blockers: Array.from(new Set(blockers)),
  };
}

@Injectable({
  providedIn: 'root',
})
export class ActivationReadinessService {
  private readonly snapshotSubject = new BehaviorSubject<ActivationEvidenceSnapshot>(
    emptyEvidenceSnapshot()
  );
  private evidenceTouched = false;

  readonly snapshot$ = this.snapshotSubject.asObservable();

  getSnapshot(): ActivationEvidenceSnapshot {
    return this.snapshotSubject.getValue();
  }

  /** Merge partial evidence from Firebase / screenshot / log callers. */
  setEvidence(partial: Partial<ActivationEvidenceSnapshot>): ActivationEvidenceSnapshot {
    this.evidenceTouched = true;
    const next = { ...this.getSnapshot(), ...partial };
    this.snapshotSubject.next(next);
    return next;
  }

  clearEvidence(): void {
    this.evidenceTouched = false;
    this.snapshotSubject.next(emptyEvidenceSnapshot());
  }

  evaluate(snapshot?: ActivationEvidenceSnapshot): ActivationReadinessResult {
    const snap = snapshot ?? this.getSnapshot();
    return evaluateActivationReadiness(snap, {
      hasEvidenceInput: snapshot ? true : this.evidenceTouched,
    });
  }

  isEarlyReviewReady(snapshot?: ActivationEvidenceSnapshot): boolean {
    return this.evaluate(snapshot).earlyReviewReady;
  }

  isDailyInsightsEligible(snapshot?: ActivationEvidenceSnapshot): boolean {
    return this.evaluate(snapshot).dailyInsightsEligible;
  }

  isFounderDayReady(snapshot?: ActivationEvidenceSnapshot): boolean {
    return this.evaluate(snapshot).founderDayReady;
  }

  isFirstSignal(snapshot?: ActivationEvidenceSnapshot): boolean {
    return this.evaluate(snapshot).firstSignal;
  }
}
